import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Users,
  CheckCircle2,
  Clock,
  Percent,
  Award,
  AlertTriangle,
  Filter,
  Download,
  Search,
  ChevronRight,
  TrendingUp,
  BarChart3,
  BookOpen,
  Calendar,
  X,
  RefreshCw,
  Eye,
  FileSpreadsheet,
  UserCheck,
  UserX,
  Layers,
  Sparkles,
  ArrowUpDown,
  GraduationCap,
  School
} from 'lucide-react';
import { SchoolAcademicProfileView } from './SchoolAcademicProfileView';
import { AnalyticsChartsGallery } from '../components/AnalyticsChartsGallery';
import { MarksEntryStatusModule } from '../components/MarksEntryStatusModule';
import { StudentAcademicHistoryModal } from '../components/StudentAcademicHistoryModal';

interface KPIStats {
  total_schools: number;
  total_students: number;
  total_enrolled_students?: number;
  current_active_students?: number;
  transferred_students?: number;
  left_school_students?: number;
  other_inactive_students?: number;
  unverified_students?: number;
  active_boys?: number;
  active_girls?: number;
  class_10_students?: number;
  class_12_students?: number;
  schools_submitted: number;
  pending_schools: number;
  completion_percentage: number;
  average_district_score: number;
  low_performing_schools: number;
  selected_month: string;
}

interface BlockStat {
  block: string;
  schools: number;
  students: number;
  submitted: number;
  pending: number;
  completion_rate: number;
  average_score: number;
  low_performing: number;
}

interface SchoolRow {
  id: number;
  school_name: string;
  school_code: string;
  block: string;
  category: string;
  total_students: number;
  submission_status: string;
  average_score: number;
  percentage: number;
  performance_category: string;
}

export const DEODashboardPage: React.FC<{ onNavigate?: (tab: string) => void }> = ({ onNavigate }) => {
  const { fetchWithAuth } = useAuth();

  // Filters
  const [selectedSession, setSelectedSession] = useState('3');
  const [selectedMonth, setSelectedMonth] = useState('August');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStream, setSelectedStream] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');

  // School dropdown list
  const [schoolsList, setSchoolsList] = useState<{ id: number; school_name: string; school_code: string; block: string }[]>([]);

  // Data states
  const [kpis, setKpis] = useState<KPIStats | null>(null);
  const [attendanceData, setAttendanceData] = useState<any>(null);
  const [satVsAssData, setSatVsAssData] = useState<any>(null);
  const [blockStats, setBlockStats] = useState<BlockStat[]>([]);
  const [chartsData, setChartsData] = useState<any>(null);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [totalSchoolsCount, setTotalSchoolsCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Student Drilldown Modal state
  const [drilldownSchool, setDrilldownSchool] = useState<SchoolRow | null>(null);
  const [drilldownStudents, setDrilldownStudents] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [studentModalOpen, setStudentModalOpen] = useState(false);

  // Safe formatting helpers
  const formatNum = (val: any, fallback = 'Data unavailable') => {
    if (val === null || val === undefined || val === '') return fallback;
    const n = Number(val);
    return isNaN(n) ? fallback : n.toLocaleString('en-IN');
  };

  const formatPct = (val: any, fallback = 'Data unavailable') => {
    if (val === null || val === undefined || val === '') return fallback;
    const n = Number(val);
    return isNaN(n) ? fallback : `${n.toFixed(1)}%`;
  };

  // Load school selector options
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const res = await fetchWithAuth(`/api/schools?block=${selectedBlock}`);
        const json = await res.json();
        if (json.success) {
          setSchoolsList(json.data || []);
        }
      } catch (e) {
        console.error('Error fetching schools:', e);
      }
    };
    fetchSchools();
  }, [selectedBlock]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const filterParams = new URLSearchParams({
        academic_session_id: selectedSession,
        month: selectedMonth,
        block: selectedBlock,
        school_id: selectedSchool,
        class_id: selectedClass,
        stream: selectedStream,
        subject_id: selectedSubject
      });

      // 1. Fetch KPIs
      const kpiRes = await fetchWithAuth(`/api/deo/dashboard-kpis?${filterParams.toString()}`);
      const kpiJson = await kpiRes.json();
      if (kpiJson.success) setKpis(kpiJson.data);

      // 2. Fetch Attendance Analysis
      const attRes = await fetchWithAuth(`/api/deo/attendance-analysis?${filterParams.toString()}`);
      const attJson = await attRes.json();
      if (attJson.success) setAttendanceData(attJson.data);

      // 3. Fetch SAT vs Assessment Month-Wise
      const satAssRes = await fetchWithAuth(`/api/deo/sat-vs-assessment?${filterParams.toString()}`);
      const satAssJson = await satAssRes.json();
      if (satAssJson.success) setSatVsAssData(satAssJson.data);

      // 4. Fetch Block Performance
      const blockRes = await fetchWithAuth(`/api/deo/block-performance?month=${selectedMonth}`);
      const blockJson = await blockRes.json();
      if (blockJson.success) setBlockStats(blockJson.data);

      // 5. Fetch 10 Charts
      const chartRes = await fetchWithAuth(`/api/deo/charts?month=${selectedMonth}&block=${selectedBlock}`);
      const chartJson = await chartRes.json();
      if (chartJson.success) setChartsData(chartJson.data);

      // 6. Fetch School Performance Table
      await loadSchoolTable();
    } catch (err) {
      console.error('Failed loading DEO dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSchoolTable = async () => {
    try {
      const query = new URLSearchParams({
        month: selectedMonth,
        block: selectedBlock,
        category: selectedCategory,
        search,
        page: String(page),
        limit: '10'
      });
      const res = await fetchWithAuth(`/api/deo/school-performance-table?${query}`);
      const json = await res.json();
      if (json.success) {
        setSchools(json.data.schools);
        setTotalSchoolsCount(json.data.pagination.total);
      }
    } catch (e) {
      console.error('Error loading school table:', e);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [selectedSession, selectedMonth, selectedBlock, selectedSchool, selectedCategory, selectedClass, selectedStream, selectedSubject]);

  useEffect(() => {
    loadSchoolTable();
  }, [page, search]);

  const openDrilldown = async (school: SchoolRow) => {
    setDrilldownSchool(school);
    try {
      const res = await fetchWithAuth(`/api/students-master?school_id=${school.id}&limit=20`);
      const json = await res.json();
      if (json.success) {
        setDrilldownStudents(json.data.students);
      }
    } catch (e) {}
  };

  const openStudentProfile = async (studentId: number) => {
    try {
      const res = await fetchWithAuth(`/api/students/${studentId}/profile`);
      const json = await res.json();
      if (json.success) {
        setSelectedStudent(json.data);
        setStudentModalOpen(true);
      }
    } catch (e) {}
  };

  const handleDownloadReport = async (type: string) => {
    try {
      const res = await fetchWithAuth(`/api/reports/download/${type}?month=${selectedMonth}&block=${selectedBlock}`);
      const json = await res.json();
      if (json.success) {
        const blob = new Blob([JSON.stringify(json.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `District_Nuh_${type}_${selectedMonth}_2026.json`;
        a.click();
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (selectedSchool && selectedSchool !== 'all') {
    return (
      <SchoolAcademicProfileView
        schoolId={parseInt(selectedSchool, 10)}
        onBack={() => setSelectedSchool('all')}
        onSelectSchool={(id) => setSelectedSchool(id.toString())}
      />
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* ---------------------------------------------------- */}
      {/* GLOBAL FILTER RIBBON                                  */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 space-y-3">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <div className="text-xs font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-amber-600" />
              <span>Government of Haryana • District Education Office, Nuh</span>
            </div>
            <h1 className="text-lg md:text-xl font-bold text-[#0F2C59] mt-0.5">
              District Assessment & SAT Monitoring Executive Dashboard
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => onNavigate && onNavigate('daily-attendance')}
              className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-bold transition flex items-center space-x-1.5 shadow-2xs"
            >
              <UserCheck className="w-3.5 h-3.5" />
              <span>Daily Attendance (6–12)</span>
            </button>

            <button
              onClick={loadDashboardData}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-xs font-bold text-slate-700 transition flex items-center space-x-1.5"
              title="Refresh Data"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-blue-600' : ''}`} />
              <span>Sync Real DB</span>
            </button>
          </div>
        </div>

        {/* Filters Matrix */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 pt-2 border-t border-slate-100 text-xs">
          {/* 1. Academic Year */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">Session</label>
            <select
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-bold text-[#0F2C59] focus:outline-none"
            >
              <option value="3">2026-27 (Current)</option>
              <option value="2">2025-26</option>
              <option value="1">2024-25</option>
            </select>
          </div>

          {/* 2. Month */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-bold text-[#0F2C59] focus:outline-none"
            >
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
              <option value="January">January</option>
              <option value="February">February</option>
            </select>
          </div>

          {/* 3. Block */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">Block</label>
            <select
              value={selectedBlock}
              onChange={(e) => {
                setSelectedBlock(e.target.value);
                setSelectedSchool('all');
              }}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-medium text-slate-900 focus:outline-none"
            >
              <option value="all">All 5 Blocks</option>
              <option value="Nuh">Nuh</option>
              <option value="Nagina">Nagina</option>
              <option value="Taoru">Taoru</option>
              <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
              <option value="Punhana">Punhana</option>
            </select>
          </div>

          {/* 4. School */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">School</label>
            <select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-medium text-slate-900 focus:outline-none truncate"
            >
              <option value="all">All Schools ({schoolsList.length})</option>
              {schoolsList.map(s => (
                <option key={s.id} value={s.id}>{s.school_name} ({s.school_code})</option>
              ))}
            </select>
          </div>

          {/* 5. Class */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-medium text-slate-900 focus:outline-none"
            >
              <option value="all">All Classes</option>
              <option value="1">Class 10</option>
              <option value="2">Class 12</option>
              <option value="6">Class 6</option>
              <option value="7">Class 7</option>
              <option value="8">Class 8</option>
              <option value="9">Class 9</option>
              <option value="11">Class 11</option>
            </select>
          </div>

          {/* 6. Stream */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">Stream</label>
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              disabled={selectedClass === '1'}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-medium text-slate-900 focus:outline-none disabled:opacity-50"
            >
              <option value="all">All Streams</option>
              <option value="Arts">Arts</option>
              <option value="Science">Science</option>
              <option value="Commerce">Commerce</option>
            </select>
          </div>

          {/* 7. Subject */}
          <div className="flex flex-col">
            <label className="font-bold text-slate-600 text-[10px] uppercase mb-0.5">Subject</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded p-1.5 font-medium text-slate-900 focus:outline-none"
            >
              <option value="all">All Subjects</option>
              <option value="1">English</option>
              <option value="2">Mathematics</option>
              <option value="3">Science</option>
              <option value="4">Physics</option>
              <option value="5">Chemistry</option>
              <option value="6">Biology</option>
              <option value="7">Hindi</option>
              <option value="8">Social Science</option>
            </select>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SECTION 1: DISTRICT OVERVIEW KPIS                    */}
      {/* ---------------------------------------------------- */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <Award className="w-4 h-4 text-blue-600" />
            <span>1. District Overview & Operational KPIs ({selectedMonth} 2026)</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {/* 1. Total Govt Schools */}
          <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
            <div className="flex items-center justify-between text-slate-500 text-[11px] font-bold uppercase">
              <span>Govt Schools</span>
              <Building2 className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <div className="text-xl font-bold text-slate-900 mt-1">
              {formatNum(kpis?.total_schools)}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
              Target in Scope
            </div>
          </div>

          {/* 2. Total Active Students */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-3.5 rounded-lg border border-emerald-300 shadow-xs">
            <div className="flex items-center justify-between text-emerald-800 text-[11px] font-bold uppercase">
              <span>Active Students</span>
              <Users className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div className="text-xl font-black text-emerald-900 mt-1">
              {formatNum(kpis?.current_active_students || kpis?.total_students)}
            </div>
            <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">
              Enrolled: {formatNum(kpis?.total_enrolled_students)}
            </div>
          </div>

          {/* 3. Schools Submitted */}
          <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
            <div className="flex items-center justify-between text-slate-500 text-[11px] font-bold uppercase">
              <span>Submitted</span>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div className="text-xl font-bold text-emerald-700 mt-1">
              {formatNum(kpis?.schools_submitted)}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
              {selectedMonth} Assessment
            </div>
          </div>

          {/* 4. Pending Schools */}
          <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
            <div className="flex items-center justify-between text-slate-500 text-[11px] font-bold uppercase">
              <span>Pending</span>
              <Clock className="w-3.5 h-3.5 text-amber-600" />
            </div>
            <div className="text-xl font-bold text-amber-700 mt-1">
              {formatNum(kpis?.pending_schools)}
            </div>
            <div className="text-[10px] text-amber-600 font-medium mt-0.5">
              Awaiting Entry
            </div>
          </div>

          {/* 5. Completion % */}
          <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
            <div className="flex items-center justify-between text-slate-500 text-[11px] font-bold uppercase">
              <span>Completion</span>
              <Percent className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <div className="text-xl font-bold text-[#0F2C59] mt-1">
              {formatPct(kpis?.completion_percentage)}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
              Verified Compliance
            </div>
          </div>

          {/* 6. Average District Score */}
          <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
            <div className="flex items-center justify-between text-slate-500 text-[11px] font-bold uppercase">
              <span>Avg Score</span>
              <Award className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <div className="text-xl font-bold text-indigo-900 mt-1">
              {formatPct(kpis?.average_district_score)}
            </div>
            <div className="text-[10px] text-slate-500 font-medium mt-0.5">
              {kpis?.students_assessed ? `${formatNum(kpis.students_assessed)} Assessed` : 'From Active Marks'}
            </div>
          </div>

          {/* 7. Low Performing Schools */}
          <div className="bg-white p-3.5 rounded-lg border border-rose-200 shadow-xs bg-rose-50/30">
            <div className="flex items-center justify-between text-rose-700 text-[11px] font-bold uppercase">
              <span>Low Perf. (&lt;40%)</span>
              <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            </div>
            <div className="text-xl font-bold text-rose-700 mt-1">
              {formatNum(kpis?.low_performing_schools)}
            </div>
            <div className="text-[10px] text-rose-600 font-semibold mt-0.5">
              Remedial Focus
            </div>
          </div>
        </div>

        {/* Student Lifecycle Position Panel */}
        <div className="bg-white rounded-xl p-3.5 shadow-sm border border-slate-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-2 border-b border-slate-100">
            <span className="text-xs font-bold text-slate-800 uppercase flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-blue-600" />
              <span>Student Lifecycle Position (Session 2026-27)</span>
            </span>
            <button
              onClick={() => onNavigate && onNavigate('student-lifecycle')}
              className="text-[11px] font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1"
            >
              <span>Manage Student Status →</span>
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-2.5 text-xs">
            <div className="p-2 bg-slate-50 rounded border border-slate-200">
              <div className="text-[10px] text-slate-500 font-bold uppercase">Total Enrolled</div>
              <div className="text-base font-bold text-slate-900">{formatNum(kpis?.total_enrolled_students)}</div>
            </div>
            <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
              <div className="text-[10px] text-emerald-800 font-bold uppercase">Current Active</div>
              <div className="text-base font-black text-emerald-900">{formatNum(kpis?.current_active_students)}</div>
            </div>
            <div className="p-2 bg-indigo-50 rounded border border-indigo-200">
              <div className="text-[10px] text-indigo-800 font-bold uppercase">Transferred</div>
              <div className="text-base font-bold text-indigo-900">{formatNum(kpis?.transferred_students)}</div>
            </div>
            <div className="p-2 bg-rose-50 rounded border border-rose-200">
              <div className="text-[10px] text-rose-800 font-bold uppercase">Left School</div>
              <div className="text-base font-bold text-rose-900">{formatNum(kpis?.left_school_students)}</div>
            </div>
            <div className="p-2 bg-amber-50 rounded border border-amber-200">
              <div className="text-[10px] text-amber-800 font-bold uppercase">Other Inactive</div>
              <div className="text-base font-bold text-amber-900">{formatNum(kpis?.other_inactive_students)}</div>
            </div>
            <div className="p-2 bg-purple-50 rounded border border-purple-200">
              <div className="text-[10px] text-purple-800 font-bold uppercase">Unverified</div>
              <div className="text-base font-bold text-purple-900">{formatNum(kpis?.unverified_students)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SECTION 2: ATTENDANCE ANALYSIS                       */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden space-y-4 p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 border-b border-slate-200 pb-3">
          <div>
            <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
              <UserCheck className="w-4 h-4 text-emerald-600" />
              <span>Departmental Attendance Analysis</span>
            </div>
            <h2 className="text-base font-bold text-[#0F2C59]">
              2. Student & Teacher Attendance Analysis (Based on Actual Database Records)
            </h2>
          </div>
          <div className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded font-semibold border border-slate-300">
            Month: <span className="font-bold text-[#0F2C59]">{selectedMonth}</span>
          </div>
        </div>

        {/* Attendance Top 9 KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Total Schools Reported */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[10px] font-bold text-slate-500 uppercase">Schools Reported</div>
            <div className="text-lg font-bold text-slate-900 mt-1">
              {attendanceData?.total_schools_reported ?? 0}
            </div>
            <div className="text-[10px] text-slate-400">With Attendance Sessions</div>
          </div>

          {/* Students Present */}
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="text-[10px] font-bold text-emerald-800 uppercase">Students Present</div>
            <div className="text-lg font-black text-emerald-900 mt-1">
              {(attendanceData?.students_present || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-emerald-600">Verified Present</div>
          </div>

          {/* Students Absent */}
          <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
            <div className="text-[10px] font-bold text-rose-800 uppercase">Students Absent</div>
            <div className="text-lg font-bold text-rose-900 mt-1">
              {(attendanceData?.students_absent || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-rose-600">Recorded Absent</div>
          </div>

          {/* Student Attendance % */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-[10px] font-bold text-blue-800 uppercase">Student Attendance %</div>
            <div className="text-lg font-black text-blue-900 mt-1">
              {attendanceData?.student_attendance_pct !== null && attendanceData?.student_attendance_pct !== undefined
                ? `${attendanceData.student_attendance_pct}%`
                : 'Data Not Available'}
            </div>
            <div className="text-[10px] text-blue-600">Overall Rate</div>
          </div>

          {/* Teachers Working */}
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="text-[10px] font-bold text-indigo-800 uppercase">Teachers Working</div>
            <div className="text-lg font-bold text-indigo-900 mt-1">
              {(attendanceData?.teachers_working || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-indigo-600">Faculty Registered</div>
          </div>
        </div>

        {/* Breakdowns Matrix (Block, Month, Class, Gender) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
          {/* A. Block-wise Attendance */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center justify-between">
              <span>A. Block-Wise Attendance</span>
              <span className="text-[10px] text-slate-500 font-normal">5 Blocks</span>
            </h3>
            <div className="space-y-1.5 text-xs">
              {attendanceData?.block_wise?.map((b: any) => (
                <div key={b.block} className="flex justify-between items-center bg-white p-2 rounded border border-slate-200">
                  <span className="font-semibold text-slate-800">{b.block}</span>
                  <div className="text-right">
                    <span className={`font-bold px-1.5 py-0.5 rounded text-[11px] ${
                      b.student_attendance_pct !== null ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {b.student_attendance_pct !== null ? `${b.student_attendance_pct}%` : 'Data Not Available'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* B. Month-wise Trend */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center justify-between">
              <span>B. Month-Wise Trend</span>
              <span className="text-[10px] text-slate-500 font-normal">Apr–Mar</span>
            </h3>
            <div className="space-y-1 text-xs max-h-56 overflow-y-auto pr-1">
              {attendanceData?.month_wise?.map((m: any) => (
                <div key={m.month} className="flex justify-between items-center py-1 px-2 rounded bg-white border border-slate-100">
                  <span className="font-medium text-slate-700">{m.month}</span>
                  <span className={`text-[11px] font-bold ${
                    m.student_attendance_pct !== null ? 'text-blue-700' : 'text-slate-400'
                  }`}>
                    {m.student_attendance_pct !== null ? `${m.student_attendance_pct}%` : '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* C. Class-wise Attendance */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center justify-between">
              <span>C. Class-Wise Attendance</span>
              <span className="text-[10px] text-slate-500 font-normal">Class 6–12</span>
            </h3>
            <div className="space-y-1.5 text-xs">
              {attendanceData?.class_wise?.map((c: any) => (
                <div key={c.class_name} className="flex justify-between items-center bg-white p-2 rounded border border-slate-200">
                  <span className="font-semibold text-slate-800">{c.class_name}</span>
                  <span className={`font-bold px-1.5 py-0.5 rounded text-[11px] ${
                    c.student_attendance_pct !== null ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {c.student_attendance_pct !== null ? `${c.student_attendance_pct}%` : 'Data Not Available'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* D. Boys vs Girls Attendance */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center justify-between">
              <span>D. Boys vs Girls Attendance</span>
              <span className="text-[10px] text-slate-500 font-normal">Gender Split</span>
            </h3>
            <div className="space-y-3 pt-1 text-xs">
              {/* Boys */}
              <div className="bg-white p-2.5 rounded border border-slate-200">
                <div className="flex justify-between text-slate-700 font-semibold mb-1">
                  <span>Boys Attendance</span>
                  <span className="font-bold text-blue-700">
                    {attendanceData?.gender_wise?.boys_attendance_pct !== null && attendanceData?.gender_wise?.boys_attendance_pct !== undefined
                      ? `${attendanceData.gender_wise.boys_attendance_pct}%`
                      : 'Data Not Available'}
                  </span>
                </div>
                {attendanceData?.gender_wise?.boys_attendance_pct !== null && (
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${attendanceData?.gender_wise?.boys_attendance_pct || 0}%` }}
                    />
                  </div>
                )}
                <div className="text-[10px] text-slate-400 mt-1">
                  Present: {attendanceData?.gender_wise?.boys_present || 0} / {attendanceData?.gender_wise?.boys_total || 0}
                </div>
              </div>

              {/* Girls */}
              <div className="bg-white p-2.5 rounded border border-slate-200">
                <div className="flex justify-between text-slate-700 font-semibold mb-1">
                  <span>Girls Attendance</span>
                  <span className="font-bold text-emerald-700">
                    {attendanceData?.gender_wise?.girls_attendance_pct !== null && attendanceData?.gender_wise?.girls_attendance_pct !== undefined
                      ? `${attendanceData.gender_wise.girls_attendance_pct}%`
                      : 'Data Not Available'}
                  </span>
                </div>
                {attendanceData?.gender_wise?.girls_attendance_pct !== null && (
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-2 rounded-full"
                      style={{ width: `${attendanceData?.gender_wise?.girls_attendance_pct || 0}%` }}
                    />
                  </div>
                )}
                <div className="text-[10px] text-slate-400 mt-1">
                  Present: {attendanceData?.gender_wise?.girls_present || 0} / {attendanceData?.gender_wise?.girls_total || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SECTION 3 & 4: SAT PERFORMANCE & MONTHLY VISUALS     */}
      {/* ---------------------------------------------------- */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <h2 className="text-base font-bold text-[#0F2C59] flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            <span>3 & 4. Monthly Assessment & SAT Analytics (Core Dimensions)</span>
          </h2>
          <span className="text-xs text-slate-500 font-medium">Dynamic Live Queries</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Chart 1: Block Completion */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
              1. Block-Wise Assessment Completion (%)
            </h3>
            <div className="space-y-2.5 text-xs">
              {chartsData?.block_completion?.map((b: any) => (
                <div key={b.block}>
                  <div className="flex justify-between text-slate-700 font-medium mb-1">
                    <span>{b.block}</span>
                    <span className="font-bold text-[#0F2C59]">{b.completion_rate}% ({b.submitted}/{b.total})</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden border border-slate-200">
                    <div
                      className="bg-[#0F2C59] h-2.5 rounded-full"
                      style={{ width: `${b.completion_rate}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 2: Block Average Score */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
              2. Block-Wise Average Score (%)
            </h3>
            <div className="space-y-2.5 text-xs">
              {chartsData?.block_average_score?.map((b: any) => (
                <div key={b.block}>
                  <div className="flex justify-between text-slate-700 font-medium mb-1">
                    <span>{b.block}</span>
                    <span className="font-bold text-indigo-700">{b.average_score}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden border border-slate-200">
                    <div
                      className="bg-indigo-600 h-2.5 rounded-full"
                      style={{ width: `${b.average_score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 3: Monthly Trend */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
              3. Monthly Assessment Progression
            </h3>
            <div className="space-y-2 text-xs max-h-48 overflow-y-auto">
              {chartsData?.monthly_trend?.map((m: any) => (
                <div key={m.month} className="flex items-center justify-between p-1.5 rounded bg-slate-50 border border-slate-200">
                  <span className="font-bold text-slate-700">{m.month}</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-[11px] text-slate-500 font-medium">Comp: {m.completion_rate}%</span>
                    <span className="font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                      {m.average_score}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 4: Class 10 Subject Performance */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
              4. Class 10 Subject Performance
            </h3>
            <div className="space-y-2.5 text-xs">
              {chartsData?.class10_subject_performance?.map((s: any) => (
                <div key={s.subject}>
                  <div className="flex justify-between text-slate-700 font-medium mb-1">
                    <span>{s.subject}</span>
                    <span className="font-bold text-slate-900">{s.average_score}% (Pass: {s.pass_rate}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${s.average_score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 5: Class 12 Subject Performance */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
              5. Class 12 Subject Performance
            </h3>
            <div className="space-y-2.5 text-xs">
              {chartsData?.class12_subject_performance?.map((s: any) => (
                <div key={s.subject}>
                  <div className="flex justify-between text-slate-700 font-medium mb-1">
                    <span>{s.subject}</span>
                    <span className="font-bold text-slate-900">{s.average_score}% (Pass: {s.pass_rate}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${s.average_score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 6: 5-Tier Performance Distribution */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
              6. District 5-Tier Distribution ({selectedMonth})
            </h3>
            <div className="space-y-2 text-xs">
              {chartsData?.performance_distribution?.map((t: any) => (
                <div key={t.range} className="flex justify-between items-center p-1.5 rounded bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className="font-semibold text-slate-800">{t.range} ({t.label})</span>
                  </div>
                  <span className="font-bold text-slate-900">{t.count} Schools ({t.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* MULTI-DIMENSIONAL VISUAL ANALYTICS (15 CHARTS)       */}
      {/* ---------------------------------------------------- */}
      <AnalyticsChartsGallery
        selectedMonth={selectedMonth}
        selectedBlock={selectedBlock}
        selectedSchool={selectedSchool}
        onSelectSchool={(sId) => setSelectedSchool(String(sId))}
      />

      {/* ---------------------------------------------------- */}
      {/* DISTRICT ASSESSMENT ENTRY MONITORING & MATRIX        */}
      {/* ---------------------------------------------------- */}
      <MarksEntryStatusModule
        defaultMonth={selectedMonth}
        defaultBlock={selectedBlock}
        defaultSchoolId={selectedSchool}
        title="District Assessment Entry Monitoring & Subject Matrix"
        onOpenStudent={(stId) => {
          setSelectedStudent({ student: { id: stId } });
          setStudentModalOpen(true);
        }}
      />

      {/* ---------------------------------------------------- */}
      {/* SECTION 5: SAT VS ASSESSMENT — MONTH-WISE             */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-[#0F2C59] text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <div className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
              Comparative Longitudinal Analysis
            </div>
            <h2 className="text-sm md:text-base font-bold">
              5. SAT vs Monthly Assessment — Month-Wise Performance Matrix
            </h2>
          </div>
          <span className="text-xs bg-amber-400 text-[#0F2C59] font-extrabold px-2.5 py-1 rounded">
            Academic Session 2026-27
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-700 uppercase font-bold border-b border-slate-200 text-[11px]">
              <tr>
                <th className="py-2.5 px-3.5">Month</th>
                <th className="py-2.5 px-3.5 text-center">SAT Average %</th>
                <th className="py-2.5 px-3.5 text-center">Assessment Average %</th>
                <th className="py-2.5 px-3.5 text-center">Difference (SAT - Ass.)</th>
                <th className="py-2.5 px-3.5 text-center">Students Assessed</th>
                <th className="py-2.5 px-3.5 text-center">SAT Students</th>
                <th className="py-2.5 px-3.5 text-center">Assessment Students</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {satVsAssData?.months?.map((row: any) => {
                const diffNum = row.difference !== null ? Number(row.difference) : null;
                return (
                  <tr key={row.month} className="hover:bg-slate-50 transition">
                    <td className="py-2.5 px-3.5 font-bold text-slate-900 flex items-center space-x-2">
                      <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                      <span>{row.month}</span>
                    </td>
                    <td className="py-2.5 px-3.5 text-center font-bold">
                      {row.sat_avg_pct !== null ? (
                        <span className="bg-indigo-50 text-indigo-800 px-2 py-0.5 rounded border border-indigo-200">
                          {row.sat_avg_pct}%
                        </span>
                      ) : (
                        <span className="text-slate-400 font-normal">Data Not Available</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3.5 text-center font-bold">
                      {row.assessment_avg_pct !== null ? (
                        <span className="bg-blue-50 text-blue-800 px-2 py-0.5 rounded border border-blue-200">
                          {row.assessment_avg_pct}%
                        </span>
                      ) : (
                        <span className="text-slate-400 font-normal">Data Not Available</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3.5 text-center font-bold">
                      {diffNum !== null ? (
                        <span className={`px-2 py-0.5 rounded border text-[11px] ${
                          diffNum > 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : diffNum < 0 ? 'bg-rose-50 text-rose-800 border-rose-300' : 'bg-slate-100 text-slate-700 border-slate-300'
                        }`}>
                          {diffNum > 0 ? `+${diffNum}%` : `${diffNum}%`}
                        </span>
                      ) : (
                        <span className="text-slate-400 font-normal">-</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3.5 text-center font-bold text-slate-800">
                      {row.students_assessed ? Number(row.students_assessed).toLocaleString() : '0'}
                    </td>
                    <td className="py-2.5 px-3.5 text-center text-slate-700">
                      {row.sat_students ? Number(row.sat_students).toLocaleString() : '0'}
                    </td>
                    <td className="py-2.5 px-3.5 text-center text-slate-700">
                      {row.assessment_students ? Number(row.assessment_students).toLocaleString() : '0'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-3 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
          <strong>Methodology Note:</strong> {satVsAssData?.methodology_note || 'Calculated using actual database marks records.'}
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SECTION 6: BLOCK-WISE PERFORMANCE MATRIX             */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-[#0F2C59] text-white flex justify-between items-center">
          <div>
            <h2 className="text-sm font-bold tracking-wide">
              6. Block-Wise Assessment Performance Matrix (All 5 Blocks)
            </h2>
            <div className="text-[11px] text-slate-300">
              Comparative Analysis for all 5 Blocks • District Nuh
            </div>
          </div>
          <span className="text-xs bg-amber-500 text-slate-950 font-bold px-2 py-0.5 rounded">
            5 Blocks Active
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-700 uppercase font-bold border-b border-slate-200 text-[11px]">
              <tr>
                <th className="py-2.5 px-3.5">Block Name</th>
                <th className="py-2.5 px-3">Schools</th>
                <th className="py-2.5 px-3">Students</th>
                <th className="py-2.5 px-3">Submitted</th>
                <th className="py-2.5 px-3">Pending</th>
                <th className="py-2.5 px-3.5">Completion Rate</th>
                <th className="py-2.5 px-3.5">Average Score</th>
                <th className="py-2.5 px-3.5">Low Perf. Schools</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {blockStats.map((b) => (
                <tr key={b.block} className="hover:bg-slate-50 transition">
                  <td className="py-2.5 px-3.5 font-bold text-slate-900 flex items-center space-x-1.5">
                    <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span>{b.block}</span>
                  </td>
                  <td className="py-2.5 px-3 text-slate-700">{formatNum(b.schools)}</td>
                  <td className="py-2.5 px-3 text-slate-700">{formatNum(b.students)}</td>
                  <td className="py-2.5 px-3 font-semibold text-emerald-700">{formatNum(b.submitted)}</td>
                  <td className="py-2.5 px-3 font-semibold text-amber-700">{formatNum(b.pending)}</td>
                  <td className="py-2.5 px-3.5">
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${Number(b.completion_rate || 0) >= 90 ? 'bg-emerald-600' : Number(b.completion_rate || 0) >= 70 ? 'bg-blue-600' : 'bg-amber-500'}`}
                          style={{ width: `${Math.min(100, Math.max(0, Number(b.completion_rate || 0)))}%` }}
                        />
                      </div>
                      <span className="font-bold text-slate-800 text-[11px]">{formatPct(b.completion_rate)}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3.5">
                    <span className={`font-bold px-2 py-0.5 rounded text-[11px] ${Number(b.average_score || 0) >= 60 ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                      {formatPct(b.average_score)}
                    </span>
                  </td>
                  <td className="py-2.5 px-3.5">
                    {Number(b.low_performing || 0) > 0 ? (
                      <span className="inline-flex items-center space-x-1 text-rose-700 font-bold bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{formatNum(b.low_performing)} School</span>
                      </span>
                    ) : (
                      <span className="text-emerald-700 font-medium">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SECTION 7: SCHOOL-WISE PERFORMANCE & DRILLDOWN       */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden space-y-3">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-slate-50">
          <div>
            <h2 className="text-sm font-bold text-[#0F2C59]">
              7. School Performance & Marks Verification Ledger
            </h2>
            <div className="text-xs text-slate-500">
              Showing {schools.length} of {totalSchoolsCount} Schools for {selectedMonth} 2026
            </div>
          </div>

          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by school or code..."
              className="w-full text-xs pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#0F2C59]"
            />
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[11px] border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3">School Code</th>
                <th className="py-2.5 px-3.5">School Name</th>
                <th className="py-2.5 px-3">Block</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-center">Active Students</th>
                <th className="py-2.5 px-3.5 text-center">Average Score</th>
                <th className="py-2.5 px-3.5 text-center">Performance Band</th>
                <th className="py-2.5 px-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {schools.map(s => (
                <tr key={s.id} className="hover:bg-slate-50 transition">
                  <td className="py-2.5 px-3 font-mono text-[11px] text-slate-600">{s.school_code}</td>
                  <td className="py-2.5 px-3.5 font-bold text-slate-900">
                    <button
                      onClick={() => setSelectedSchool(s.id.toString())}
                      className="text-left font-bold text-[#0F2C59] hover:underline hover:text-blue-700 flex items-center space-x-1"
                      title="Open Full Academic Profile"
                    >
                      <span>{s.school_name}</span>
                      <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
                    </button>
                  </td>
                  <td className="py-2.5 px-3 text-slate-700">{s.block}</td>
                  <td className="py-2.5 px-3 text-slate-600">{s.category}</td>
                  <td className="py-2.5 px-3 text-center font-bold text-slate-800">{s.total_students}</td>
                  <td className="py-2.5 px-3.5 text-center font-black text-slate-900">{s.average_score}%</td>
                  <td className="py-2.5 px-3.5 text-center">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${
                      s.average_score >= 90 ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                      s.average_score >= 75 ? 'bg-indigo-50 text-indigo-800 border-indigo-200' :
                      s.average_score >= 60 ? 'bg-blue-50 text-blue-800 border-blue-200' :
                      s.average_score >= 40 ? 'bg-amber-50 text-amber-800 border-amber-200' :
                      'bg-rose-50 text-rose-800 border-rose-200'
                    }`}>
                      {s.performance_category}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        onClick={() => setSelectedSchool(s.id.toString())}
                        className="px-2 py-1 bg-[#0F2C59] hover:bg-[#1E3A8A] text-white rounded font-bold text-[10px] transition flex items-center space-x-1 shadow-2xs"
                        title="View Complete Academic Performance Profile"
                      >
                        <Award className="w-3 h-3 text-amber-400" />
                        <span>Profile</span>
                      </button>
                      <button
                        onClick={() => openDrilldown(s)}
                        className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-semibold text-[10px] transition flex items-center space-x-1"
                        title="View Student Roster"
                      >
                        <Eye className="w-3 h-3 text-blue-600" />
                        <span>Roster</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-xs">
          <span className="text-slate-600">Page {page} of {Math.ceil(totalSchoolsCount / 10) || 1}</span>
          <div className="space-x-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded bg-white font-semibold disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(totalSchoolsCount / 10)}
              className="px-3 py-1 border rounded bg-white font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SECTION 8: DOWNLOAD / EXPORT REPORTS                 */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <h2 className="text-sm font-bold text-[#0F2C59] flex items-center gap-1.5">
            <Download className="w-4 h-4 text-emerald-600" />
            <span>8. Official Download & Export Reports (Government Formats)</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-1">
          <button
            onClick={() => handleDownloadReport('district')}
            className="p-3 rounded-lg border border-slate-300 hover:border-[#0F2C59] bg-slate-50 hover:bg-white text-left transition flex items-center justify-between group"
          >
            <div>
              <div className="font-bold text-slate-900 text-xs group-hover:text-[#0F2C59]">District Summary Report</div>
              <div className="text-[10px] text-slate-500 mt-0.5">5-Block Comprehensive Data</div>
            </div>
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
          </button>

          <button
            onClick={() => handleDownloadReport('block')}
            className="p-3 rounded-lg border border-slate-300 hover:border-[#0F2C59] bg-slate-50 hover:bg-white text-left transition flex items-center justify-between group"
          >
            <div>
              <div className="font-bold text-slate-900 text-xs group-hover:text-[#0F2C59]">Block Assessment Dossier</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Block {selectedBlock} Schools</div>
            </div>
            <FileSpreadsheet className="w-4 h-4 text-blue-600" />
          </button>

          <button
            onClick={() => handleDownloadReport('class10')}
            className="p-3 rounded-lg border border-slate-300 hover:border-[#0F2C59] bg-slate-50 hover:bg-white text-left transition flex items-center justify-between group"
          >
            <div>
              <div className="font-bold text-slate-900 text-xs group-hover:text-[#0F2C59]">Class 10 Analytics Dossier</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Subject-Wise Diagnostics</div>
            </div>
            <FileSpreadsheet className="w-4 h-4 text-indigo-600" />
          </button>

          <button
            onClick={() => handleDownloadReport('class12')}
            className="p-3 rounded-lg border border-slate-300 hover:border-[#0F2C59] bg-slate-50 hover:bg-white text-left transition flex items-center justify-between group"
          >
            <div>
              <div className="font-bold text-slate-900 text-xs group-hover:text-[#0F2C59]">Class 12 Analytics Dossier</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Stream-Wise Diagnostics</div>
            </div>
            <FileSpreadsheet className="w-4 h-4 text-purple-600" />
          </button>
        </div>
      </div>

      {/* Drilldown Modal */}
      {drilldownSchool && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-slate-200">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold text-sm">{drilldownSchool.school_name}</h3>
                <div className="text-xs text-slate-300">Code: {drilldownSchool.school_code} • Block: {drilldownSchool.block}</div>
              </div>
              <button onClick={() => setDrilldownSchool(null)} className="p-1 text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[11px]">
                  <tr>
                    <th className="py-2 px-3">SRN</th>
                    <th className="py-2 px-3">Student Name</th>
                    <th className="py-2 px-3">Father Name</th>
                    <th className="py-2 px-3">Class</th>
                    <th className="py-2 px-3">Gender</th>
                    <th className="py-2 px-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium">
                  {drilldownStudents.map(st => (
                    <tr key={st.id} className="hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono text-slate-600">{st.srn}</td>
                      <td className="py-2 px-3 font-bold text-slate-900">{st.student_name}</td>
                      <td className="py-2 px-3 text-slate-600">{st.father_name}</td>
                      <td className="py-2 px-3 text-slate-700">{st.class_name || (st.class_id === 1 ? 'Class 10' : 'Class 12')}</td>
                      <td className="py-2 px-3 text-slate-600">{st.gender}</td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={() => openStudentProfile(st.id)}
                          className="px-2 py-0.5 bg-blue-50 text-blue-700 font-semibold rounded text-[11px] hover:bg-blue-100"
                        >
                          360° Profile
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Student 360 Profile Modal */}
      {studentModalOpen && selectedStudent && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-slate-200">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold text-sm">{selectedStudent.student?.student_name}</h3>
                <div className="text-xs text-slate-300">SRN: {selectedStudent.student?.srn} • {selectedStudent.student?.school_name}</div>
              </div>
              <button onClick={() => setStudentModalOpen(false)} className="p-1 text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2 bg-slate-50 rounded border">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Class</div>
                  <div className="font-bold text-slate-800">{selectedStudent.student?.class_name || 'Class 10'}</div>
                </div>
                <div className="p-2 bg-slate-50 rounded border">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Father's Name</div>
                  <div className="font-bold text-slate-800">{selectedStudent.student?.father_name || '-'}</div>
                </div>
                <div className="p-2 bg-slate-50 rounded border">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Overall Average</div>
                  <div className="font-bold text-emerald-700">{selectedStudent.overall_average || '0.0'}%</div>
                </div>
                <div className="p-2 bg-slate-50 rounded border">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Attendance</div>
                  <div className="font-bold text-blue-700">{selectedStudent.attendance_percentage !== null ? `${selectedStudent.attendance_percentage}%` : 'Data Not Available'}</div>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-800 uppercase text-[11px] mb-2">Historical Marks Record</h4>
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 font-bold text-slate-700">
                    <tr>
                      <th className="p-2">Month</th>
                      <th className="p-2">Subject</th>
                      <th className="p-2 text-center">Marks</th>
                      <th className="p-2 text-right">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {selectedStudent.marks_history?.map((m: any, idx: number) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-2 font-semibold">{m.month}</td>
                        <td className="p-2">{m.subject}</td>
                        <td className="p-2 text-center font-mono">{m.is_absent ? 'ABSENT' : `${m.marks}/${m.max}`}</td>
                        <td className="p-2 text-right font-bold text-slate-800">{m.is_absent ? '-' : `${m.percentage}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
